import { useState } from "react";
import { Flag, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const T = {
  en: { link: "Picture missing or wrong?", done: "Reported, thank you!", title: "What's the problem?", ph: "Tell us more (optional)",
    phOther: "Tell us more (required)", send: "Send", reasons: { missing: "Picture missing", wrong: "Wrong picture", shows_answer: "Shows the answer", other: "Other" } },
  fr: { link: "Image manquante ou fausse ?", done: "Signalé, merci !", title: "Quel est le problème ?", ph: "Dites-nous en plus (facultatif)",
    phOther: "Dites-nous en plus (obligatoire)", send: "Envoyer", reasons: { missing: "Image manquante", wrong: "Mauvaise image", shows_answer: "Montre la réponse", other: "Autre" } },
};

export default function ReportQuestion({ payload, lang = "en", reported, onReported }) {
  const t = T[lang === "fr" ? "fr" : "en"];
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = reason && (reason !== "other" || note.trim());

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/question-report", { ...payload, reason, note: note.trim() });
      toast.success(t.done);
      setOpen(false);
      onReported();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (reported) return <p className="mt-2 text-xs font-parent text-emerald-700 inline-flex items-center gap-1" data-testid="report-question-done"><Check size={13} /> {t.done}</p>;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="report-question-link"
          className="mt-2 text-xs font-parent text-slate-500 underline underline-offset-2 hover:text-rose-600 inline-flex items-center gap-1">
          <Flag size={12} /> {t.link}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start" data-testid="report-question-form">
        <p className="font-bold text-sm mb-2">{t.title}</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {Object.entries(t.reasons).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setReason(id)} data-testid={`report-reason-${id}`}
              className={`px-2.5 py-1 rounded-full border-2 text-xs font-semibold transition-colors ${reason === id ? "bg-rose-500 border-rose-500 text-white" : "border-slate-300 text-slate-700 hover:border-rose-300"}`}>
              {label}
            </button>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} data-testid="report-note"
          placeholder={reason === "other" ? t.phOther : t.ph}
          className="w-full text-sm rounded-lg border-2 border-slate-200 p-2 outline-none focus:border-rose-300 resize-none" />
        <button type="button" onClick={send} disabled={busy || !valid} data-testid="report-submit"
          className="mt-2 w-full rounded-full bg-rose-500 text-white font-bold text-sm py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />} {t.send}
        </button>
      </PopoverContent>
    </Popover>
  );
}
